/**
 * Raise Alert feature — API calls for the critical finding alert feature.
 *
 * Both calls swallow their errors on purpose. This feature must never stop a
 * radiologist submitting a report or opening the studylist, so a failure here
 * returns "off" or "nothing" rather than throwing.
 *
 * NOTE: the URLs are built inside the functions, not as module-level constants.
 * API_BASE_URL in Constants.jsx is computed at module evaluation time, and
 * Constants.jsx pulls in the whole cornerstone tools graph — so reading it at
 * the top of this module can run before it is initialised and throw
 * "Cannot access 'API_BASE_URL' before initialization", which blanks the app.
 * Every other service in src/service builds its URL inside the call for the
 * same reason.
 */
import { API_BASE_URL } from "../constants/Constants";
import axiosInterceptor from "../helpers/Interceptor";

/**
 * Fetches the rules, suppression words, modality map and the feature flag.
 * Called once per session — the engine caches the result.
 *
 * @returns the config object, or null if it could not be fetched
 */
export const fetchCriticalAlertConfig = async () => {
  try {
    const response = await axiosInterceptor.get(
      API_BASE_URL + "jwtServices/critical-alert/rules",
      { headers: { "content-type": "application/json" } }
    );
    // backend wraps everything in GenericResponseBuilder
    return response?.data?.data || null;
  } catch (error) {
    console.warn(
      "[Raise Alert feature] could not fetch alert rules — feature stays off:",
      error?.message
    );
    return null;
  }
};

/**
 * The findings already raised on a study, for the ALERT RAISED badge modal.
 *
 * @returns an array of findings, empty if none or on failure
 */
export const fetchStudyCriticalAlerts = async (studyId) => {
  if (!studyId) {
    return [];
  }
  try {
    const response = await axiosInterceptor.get(
      API_BASE_URL + "jwtServices/critical-alert/study/" + studyId,
      { headers: { "content-type": "application/json" } }
    );
    return response?.data?.data || [];
  } catch (error) {
    console.warn(
      "[Raise Alert feature] could not fetch raised findings for study",
      studyId,
      error?.message
    );
    return [];
  }
};